import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './components/Toast';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import EmailVerificationPage from './pages/EmailVerificationPage';
import DashboardPage from './pages/DashboardPage';
import CreateAuraPage from './pages/CreateAuraPage';
import AuraPage from './pages/AuraPage';
import PortalPage from './pages/PortalPage';
import AuraCastPage from './pages/AuraCastPage';
import PricingPage from './pages/PricingPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import BrandSettingsPage from './pages/BrandSettingsPage';
import FindMePage from './pages/FindMePage';
import MyMomentsPage from './pages/MyMomentsPage';
import EventSitePage from './pages/EventSitePage';
import AuraSiteBuilderPage from './pages/AuraSiteBuilderPage';
import PhotoBookPage from './pages/PhotoBookPage';
import CalendarPage from './pages/CalendarPage';
import PrintOrderPage from './pages/PrintOrderPage';
import StatusPage from './pages/StatusPage';
import AccountDataPage from './pages/AccountDataPage';
import OrgSettingsPage from './pages/OrgSettingsPage';
import OrgDashboardPage from './pages/OrgDashboardPage';
import AgencyDashboardPage from './pages/AgencyDashboardPage';
import AccountIntegrationsPage from './pages/AccountIntegrationsPage';
import AuraCastProjectionPage from './pages/AuraCastProjectionPage';
import PortalKioskPage from './pages/PortalKioskPage';
import GroupGalleryPage from './pages/GroupGalleryPage';
import SeatingPage from './pages/SeatingPage';
import AuraCastPlaylistPage from './pages/AuraCastPlaylistPage';
import LiveViewPage from './pages/LiveViewPage';
import ErrorBoundary from './components/ErrorBoundary';
import ImpersonationBanner from './components/ImpersonationBanner';
import MaintenanceBanner from './components/MaintenanceBanner';
import './App.css';

// Code-split heavy / rarely-visited routes
const AdminDashboardPage = React.lazy(() => import('./pages/AdminDashboardPage'));
const BlogIndexPage = React.lazy(() => import('./pages/BlogIndexPage'));
const BlogPostPage = React.lazy(() => import('./pages/BlogPostPage'));
const UseCasePage = React.lazy(() => import('./pages/UseCasePage'));
const AboutPage = React.lazy(() => import('./pages/AboutPage'));
const PrintPolicyPage = React.lazy(() => import('./pages/PrintPolicyPage'));
const CityUseCasePage = React.lazy(() => import('./pages/CityUseCasePage'));
const CityUseCaseIndexPage = React.lazy(() =>
  import('./pages/CityUseCasePage').then(m => ({ default: m.CityUseCaseIndexPage }))
);

// Optional Sentry/GlitchTip-compatible error reporting — env-gated, dynamic import
// so the SDK isn't bundled into the critical path when unused.
if (process.env.REACT_APP_SENTRY_DSN) {
  import('@sentry/react').then(Sentry => {
    Sentry.init({
      dsn: process.env.REACT_APP_SENTRY_DSN,
      tracesSampleRate: 0.05,
      environment: process.env.REACT_APP_ENV || 'prod',
    });
  }).catch(() => { /* SDK optional — never break boot */ });
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen bg-midnight flex items-center justify-center"><div className="text-champagne animate-pulse font-serif text-xl">Loading...</div></div>;
  // Remember where the visitor was going (e.g. the aura link in a closing-ceremony
  // email) so the login page can send them back there.
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  return children;
}

// Short link printed on the aura card: /p/:auraKey -> /portal/:auraKey
function PortalShortRedirect() {
  const { auraKey } = useParams();
  return <Navigate to={`/portal/${auraKey}`} replace />;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-midnight flex items-center justify-center"><div className="text-champagne animate-pulse font-serif text-xl">Loading...</div></div>;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

const ADMIN_PANEL_ROLES = ['support', 'billing', 'auditor', 'admin', 'superadmin'];
function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-midnight flex items-center justify-center"><div className="text-champagne animate-pulse font-serif text-xl">Loading...</div></div>;
  if (!user || !ADMIN_PANEL_ROLES.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      {/* Auth utility pages — plain (no PublicRoute) so logged-in users can still verify/reset */}
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<EmailVerificationPage />} />
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/create-aura" element={<ProtectedRoute><CreateAuraPage /></ProtectedRoute>} />
      <Route path="/aura/:auraId" element={<ProtectedRoute><AuraPage /></ProtectedRoute>} />
      <Route path="/portal/:auraKey" element={<PortalPage />} />
      <Route path="/p/:auraKey" element={<PortalShortRedirect />} />
      <Route path="/portal/:auraKey/kiosk" element={<PortalKioskPage />} />
      <Route path="/auracast/:auraKey" element={<AuraCastPage />} />
      <Route path="/auracast/:auraKey/projection" element={<AuraCastProjectionPage />} />
      <Route path="/live/:token" element={<LiveViewPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccessPage /></ProtectedRoute>} />
      <Route path="/admin" element={<AdminRoute><AdminDashboardPage /></AdminRoute>} />
      <Route path="/settings/brand" element={<ProtectedRoute><BrandSettingsPage /></ProtectedRoute>} />
      <Route path="/portal/:auraKey/find-me" element={<FindMePage />} />
      <Route path="/my-moments/:token" element={<MyMomentsPage />} />
      <Route path="/event/:auraKey" element={<EventSitePage />} />
      <Route path="/aura/:auraId/site" element={<ProtectedRoute><AuraSiteBuilderPage /></ProtectedRoute>} />
      <Route path="/aura/:auraId/photo-book" element={<ProtectedRoute><PhotoBookPage /></ProtectedRoute>} />
      <Route path="/aura/:auraId/calendar" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
      <Route path="/aura/:auraId/print" element={<ProtectedRoute><PrintOrderPage /></ProtectedRoute>} />
      <Route path="/status" element={<StatusPage />} />
      <Route path="/account/data" element={<ProtectedRoute><AccountDataPage /></ProtectedRoute>} />
      <Route path="/account/integrations" element={<ProtectedRoute><AccountIntegrationsPage /></ProtectedRoute>} />
      <Route path="/org/settings" element={<ProtectedRoute><OrgSettingsPage /></ProtectedRoute>} />
      <Route path="/org/dashboard" element={<ProtectedRoute><OrgDashboardPage /></ProtectedRoute>} />
      <Route path="/agency" element={<ProtectedRoute><AgencyDashboardPage /></ProtectedRoute>} />
      <Route path="/group/:key" element={<GroupGalleryPage />} />
      <Route path="/use-cases/:slug" element={<UseCasePage />} />
      <Route path="/events" element={<CityUseCaseIndexPage />} />
      <Route path="/events/:city/:useCase" element={<CityUseCasePage />} />
      <Route path="/blog" element={<BlogIndexPage />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/policies/print-orders" element={<PrintPolicyPage />} />
      <Route path="/aura/:auraId/seating" element={<ProtectedRoute><SeatingPage /></ProtectedRoute>} />
      <Route path="/auracast/playlist/:orgId" element={<ProtectedRoute><AuraCastPlaylistPage /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <ToastProvider>
          <AuthProvider>
            <Router>
              <ImpersonationBanner />
              <MaintenanceBanner />
              <Suspense fallback={<div className="min-h-screen bg-midnight flex items-center justify-center"><div className="text-champagne animate-pulse font-serif">Loading…</div></div>}>
                <AppRoutes />
              </Suspense>
            </Router>
          </AuthProvider>
        </ToastProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
